import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  TextField,
  InputAdornment,
} from "@mui/material";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { popupType } from "../../../types/types";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { Loader } from "../../../components/loader/Loader";
import { ConfirmationPopUp } from "../../../components/popup/confirmationPopUp";
import { GenericPopUp } from "../../../components/popup/genericPopUp";
import FormHeader from "../../../components/dms_common/FormHeader";
import Header from "../../../components/dms_common/Header";
import { viewClaimMetaData1, viewClaimMetaData2 } from "./ViewClaimDetailsUtils";
import { getViewClaimsDetailsColumnsMetaData } from "../../../features/claimsNew/utils/ViewClaimsUtils";
import { formatDecimal } from "../../../components/dms_common/DmsUtils";
import { getBlobUrlForSku, ApprovalConfig, transformApprovalData } from "../../../features/claimsNew/utils/utils";
import { setViewClaimsRow, setSelectedViewClaimsRow } from "../../../stateManagement/actions/claims";
import { useSelector, useDispatch } from "react-redux";
import CustomAccordian from "../../../components/dms_common/CustomAccordian";
import { getClaimDetails, getClaimApprovalDetails } from "../../../services/claimService";
import ClaimDetailsModal from './ClaimDetailsModal';
import store from "../../../stateManagement/store";
import ViewClaimsDetailsModal from './ViewClaimsDetailsModal';
import { capitalize } from "../../../utils/utils";
import { getConfigData } from "../../../services/configurationService";
import { getMetaData } from "../../../services/uiConfigService";
import { getDataFromLocalDb } from "../../../services/dbService";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import { logLoadTrace } from "../../../../src/firebase";
import { createScrollHandler, handleScroll } from "../../../components/dms_common/DmsUtils";

const ViewClaimsDetails = () => {
  const { viewClaimsRows, selectedViewClaimsRow } = useSelector(
    (state: any) => state.claims
  );
  const sidebarCollapsed: boolean = useSelector(
    () => store.getState().sidebarState.sidebarCollapsed
  );
  const navigate = useNavigate();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const query = useQuery();
  const claimId = query.get('claimId') || "0";
  const dispatch = useDispatch();
  const [rows, setRows] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [loader, setLoader] = useState(true);
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [confirmationPopUp, setConfirmationPopUp] = useState(false);
  const [popUpMessage, setPopUpMessage] = useState<string>("");
  const [actionButtons, setActionButtons] = useState<any[]>([]);
  const [accordionData, setAccordionData] = useState<any>({});
  const [isScrolling, setIsScrolling] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const popUpSuccessMethod = useRef<any>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [approvalConfig, setApprovalConfig] = useState<ApprovalConfig | null>(null);
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { caseUnitLabel, otherUnitLabel, packUnitLabel, currency, claimTypes, claimSubTypes, batchIdConfiguration, displayOtherUnitName, enableFocPurchaseReturnClaims } = ConfigDictionary;
  const currentDesignation = JSON.parse(localStorage.authContext)?.user?.designation[0];
  const claimsDetailsColumnsMetaData = getViewClaimsDetailsColumnsMetaData(accordionData?.claimType, accordionData?.typeOfExpense, claimTypes, claimSubTypes, enableFocPurchaseReturnClaims);

  const [scrollTop, setScrollTop] = useState<number>(0); 
  const scrollRef = useRef<HTMLDivElement>(null);
  const [allowTableScroll, setAllowTableScroll] = useState<boolean>(false);
  const [dontAllowContainerScroll, setDontContainerScroll] = useState(true);

  const openPopup = useCallback((message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  }, []);

  const onCustomAccordionToggle = (isExpanded: boolean) => {
    setIsScrolling(!isExpanded);
  }

  const filterData = (searchedValue: string) => {
    setSearchValue(searchedValue); 
    if (!searchedValue) {
      setRows(viewClaimsRows); 
      return;
    }
    searchedValue = searchedValue.toLowerCase().replace(/\s/g, "");
    const finalData = data.filter((eachRow: any) => {
      const allValues = Object.values(eachRow);
      return allValues.some((eachValue: any) => {
        if (typeof eachValue === "string") {
          const formattedValue = eachValue.toLowerCase().replace(/\s/g, "");
          return formattedValue.includes(searchedValue);
        }
        return false;
      });
    });
    const sortedData = sortRows(
      finalData,
      accordionData.claimType,
      accordionData.typeOfExpense,
    );
    setRows(sortedData);
  };

  useEffect(() => {
    setSearchValue(""); 
    setRows(viewClaimsRows);
  }, [viewClaimsRows]);

  const toTitleCase = (str: string) => str.replace(/\w\S*/g, (txt) => capitalize(txt.toLowerCase()));

  const formatClaimSubType = (subType: string): string => {
    if (subType.toLowerCase() === "asset") {
      return claimSubTypes[5];
    }
    if (subType.toLowerCase() === "banner") {
      return claimSubTypes[4];
    }
    if (subType.toLowerCase() === "allexcltov") {
      return claimSubTypes[2]
    }
    if (subType.toLowerCase() === "totalordervalue") {
      return claimSubTypes[3]
    }

    return subType
      .replace(/(RETURN|RETURNS)/gi, " Return")
      .split(" ")
      .map(toTitleCase)
      .join(" ");
  };

  const formatClaimType = (type: string): string => {
    if (type.toLowerCase() === claimTypes[2].toLowerCase().replace(" ", "")) {
      return claimTypes[2];
    }
    if (type.toLowerCase() === claimTypes[0].toLowerCase().replace(" ", "")) {
      return claimTypes[0];
    }
    if (type.toLowerCase() === claimTypes[1].toLowerCase().replace(" ", "")) {
      return claimTypes[1];
    }
    return toTitleCase(type);
  };

  const sortRows = (rows: any[], claimType: string, claimSubType: string) => {
    const sortedRows = [...rows];
    const formattedClaimType = formatClaimType(claimType);
    const formattedSubType = formatClaimSubType(claimSubType);
    if (formattedClaimType === claimTypes[2]) {
      return sortedRows.sort((a, b) =>
        (a.outletCode || '').localeCompare(b.outletCode || '')
      );
    }

    if (formattedClaimType === claimTypes[0] || formattedSubType === claimSubTypes[2]) {
      return sortedRows.sort((a, b) =>
        (a.skuCode || '').localeCompare(b.skuCode || '')
      );
    }

    if (formattedSubType === claimSubTypes[3]) {
      return sortedRows.sort((a, b) =>
        (a.returnOrderNo || '').localeCompare(b.returnOrderNo || '')
      );
    }
    return sortedRows;
  };

  const getActionButtons = async () => {
    try {
      const stages = await getConfigData(
        "dms_configuration",
        "claimStages"
      );
      setActionButtons(stages[0]?.configValue ?? []);
    } catch (error) {
      console.error(error);
    }
  };

  const processApprovalConfig = (configResponse: any[]): ApprovalConfig | null => {
    const claimApprovalConfig = configResponse.find(config => 
      config.hasOwnProperty('CLAIM_APPROVAL')
    )?.CLAIM_APPROVAL;
  
    if (!claimApprovalConfig || !claimApprovalConfig.workflow) {
      return null;
    }
  
    const transformedConfig: ApprovalConfig = {
      flow: claimApprovalConfig.workflow.map((level: any) => 
        level.approvers.map((approver: any) => approver.designation)
      ),
      currentLevel: undefined
    };
  
    for (let i = 0; i < transformedConfig.flow.length; i++) {
      if (transformedConfig.flow[i].includes(currentDesignation)) {
        transformedConfig.currentLevel = i;
        break;
      }
    }
  
    return transformedConfig;
  };

  const getAllDataForClaim = async (claimId: string) => {
    try {
      const [claimDetailsResponse, claimApprovalResponse, configResponse] = await Promise.all([
        getClaimDetails(claimId),
        getClaimApprovalDetails(claimId),
        getMetaData("NewApprovalFlow","RequestConfig")
      ]);
      const claimData = claimDetailsResponse.data.features?.[0];

      const approvalData = transformApprovalData(claimApprovalResponse.data.features);
      const processedConfig = processApprovalConfig(configResponse);
      setApprovalConfig(processedConfig);
      if (claimData) {
        const {
          creationTime,
          claimApprovalDate,
          claimType,
          claimSubType,
          claimTotalValue,
          claimApprovedValue,
          claimTotalQuantity,
          claimDetails,
          status,
        } = claimData;
        const approvalMap = new Map<string, any[]>();
        approvalData.forEach((approval: any) => {
          if (!approvalMap.has(approval.claimDetailsId)) {
            approvalMap.set(approval.claimDetailsId, []);
          }
          approvalMap.get(approval.claimDetailsId)!.push(approval);
        });

        const updatedClaimDetails = claimDetails.map((detail: any) => ({
          ...detail,
          approvals: approvalMap.get(detail.id) || [],
        }));
        const claimDate = dayjs(creationTime).format("DD-MM-YYYY");
        const blobUrlMap: Record<string, string | null> = {};
        await Promise.all(
          updatedClaimDetails.map(async (detail: any) => {
            if (!blobUrlMap[detail.skuCode]) {
              blobUrlMap[detail.skuCode] = await getBlobUrlForSku(detail.skuCode);
            }
          })
        );
        const approvalDate = claimApprovalDate ? dayjs(claimApprovalDate).format("DD-MM-YYYY") : "-";
        const convertDateToMonthYear = (dateString: string | undefined): string | undefined => {
          if (!dateString) return undefined;
          try {
            return dayjs(dateString).format("MMM YYYY");
          } catch {
            return undefined;
          }
        };
        const selectedMonth = claimType !== claimTypes[2].toUpperCase() ? convertDateToMonthYear(claimDetails[0]?.displayStartDate) : dayjs(creationTime).format("MMM YYYY");
        const claimQty =
          claimType === claimTypes[2].toUpperCase()
            ? { cases: 0, pieces: 0, others: 0 }
            : (() => {
              const [cases = 0, pieces = 0, others = 0] = claimTotalQuantity
                .split(".")
                .map(Number);
              return { cases, pieces, others };
            })();

        const assetQty =
          claimType === claimTypes[2].toUpperCase()
            ? claimDetails.reduce((acc: number, detail: any) => {
              return acc + (detail.assetQty ? Number(detail.assetQty) : 0);
            }, 0)
            : 0;

        const approvedQty = claimDetails.reduce(
          (acc: { cases: number; pieces: number; others: number }, detail: any) => {
            if (claimType === claimTypes[2].toUpperCase()) {
              const totalApproved = Number(detail.approvedQty) || 0;
              acc.cases += totalApproved;
              acc.pieces = 0;
              acc.others = 0;
            } else {
              const qtys = detail.approvedQty.split(".").map((val: any) => {
                const num = Number(val);
                return isNaN(num) ? 0 : num;
              });

              const [cases, pieces, others] = qtys.length === 3 ? qtys : [0, 0, 0];

              acc.cases += cases || 0;
              acc.pieces += pieces || 0;
              acc.others += others || 0;
            }

            return acc;
          },
          { cases: 0, pieces: 0, others: 0 }
        );

        const formattedStatus = status.includes("_") ? status.split("_").join(" ") : status;
        const rejectedCase = claimQty.cases - approvedQty.cases;
        const rejectedPcs = claimQty.pieces - approvedQty.pieces;
        const rejectedOthers = claimQty.others - approvedQty.others;

        const formatQuantities = (cases: number, others: number, pieces: number) =>
          [
            caseUnitLabel && `${cases} ${caseUnitLabel}`,
            otherUnitLabel && `${others} ${otherUnitLabel}`,
            packUnitLabel && `${pieces} ${packUnitLabel}`
          ].filter(Boolean).join(", ");

        const totalQuantities = formatQuantities(claimQty.cases, claimQty.others, claimQty.pieces);
        const approvedTotalQuantities = formatQuantities(approvedQty.cases, approvedQty.others, approvedQty.pieces);
        const rejectedTotalQuantities = formatQuantities(rejectedCase, rejectedOthers, rejectedPcs);

        const responseAccordion = {
          claimDate,
          approvalDate,
          claimType: formatClaimType(claimType),
          typeOfExpense: formatClaimSubType(claimSubType),
          selectedMonth,
          assetQty,
          assetApprovedQty: claimType === claimTypes[2].toUpperCase() ? approvedQty.cases : 0,
          totalQuantities,
          approvedTotalQuantities,
          rejectedTotalQuantities,
          claimAmount: `${currency} ${formatDecimal(claimTotalValue)}`,
          approvedClaimAmount: `${currency} ${formatDecimal(claimApprovedValue)}`,
          status: formattedStatus
        };

        let products: Record<string, any> = {};
        if (displayOtherUnitName) {
          const dbResult = await getDataFromLocalDb("Product");
          products = dbResult?.data?.[0]?.data ?? {};
        }
        let customOtherUnitLabel = otherUnitLabel;

        let responseArray;
        if (formatClaimSubType(claimSubType) === claimSubTypes[0]) {
          const groupedBySkuAndReference = updatedClaimDetails.reduce((acc: any, detail: any) => {
            const skuCode = detail.skuCode;
            const referenceNumber = detail.referenceNumber;
            const key = `${skuCode}-${referenceNumber}`;
            const imgBlobUrl = blobUrlMap[skuCode];
            if (displayOtherUnitName) {
              const product = products[skuCode];
              if (product?.otherUnitName && product.otherUnitName.length > 0) {
                customOtherUnitLabel = product.otherUnitName;
              }
            }
            if (!acc[key]) {
              acc[key] = {
                skuCode,
                skuName: detail.skuName,
                returnOrderNo: referenceNumber,
                ptdCs: detail.extendedAttributes.ptdCs,
                ptdPc: detail.extendedAttributes.ptdPc,
                ptdOther: detail.extendedAttributes.ptdOther,
                batchIds: [],
                damageCase: 0,
                damagePcs: 0,
                damageOthers: 0,
                approvedCase: 0,
                approvedPcs: 0,
                approvedOthers: 0,
                claimAmount: 0,
                approvedClaimAmount: 0,
                imgBlobUrl,
                otherUnitName: customOtherUnitLabel
              };
            }

            const [damageCases, damagePcs, damageOthers] = detail.claimedQty.split(".").map(Number);
            const [approvedCases, approvedPcs, approvedOthers] = detail.approvedQty.split(".").map(Number);
            acc[key].damageCase += damageCases || 0;
            acc[key].damagePcs += damagePcs || 0;
            acc[key].damageOthers += damageOthers || 0;
            acc[key].approvedCase += approvedCases || 0;
            acc[key].approvedPcs += approvedPcs || 0;
            acc[key].approvedOthers += approvedOthers || 0;
            acc[key].claimAmount += detail.claimedAmount || 0;
            acc[key].approvedClaimAmount += detail.approvedAmount || 0;

            let batch = acc[key].batchIds.find((b: any) => b.batchId === detail.batchCode);
            if (!batch) {
              batch = {
                batchId: detail.batchCode,
                claimableCs: 0,
                claimablePc: 0,
                claimableOther: 0,
                approvedCs: 0,
                approvedPc: 0,
                approvedOther: 0,
                returnReasonArray: [],
                claimsRejectionReason: detail.rejectionReason,
                claimDetailsIds: [],
                approvals: [],
              };
              acc[key].batchIds.push(batch);
            }

            batch.returnReasonArray.push({
              cs: damageCases || 0,
              pc: damagePcs || 0,
              other: damageOthers || 0,
              approvedCs: approvedCases || 0,
              approvedPc: approvedPcs || 0,
              approvedOther: approvedOthers || 0,
              reason: detail.remark,
              claimDetailsId: detail.id,
              approvals: detail.approvals
            });

            batch.claimableCs += damageCases || 0;
            batch.claimablePc += damagePcs || 0;
            batch.claimableOther += damageOthers || 0;
            batch.approvedCs += approvedCases || 0;
            batch.approvedPc += approvedPcs || 0;
            batch.approvedOther += approvedOthers || 0;
            batch.claimsApprovedAmount += detail.approvedAmount || 0;
            batch.claimDetailsIds.push(detail.id);
            batch.approvals.push(...detail.approvals);
            const { ptdCs, ptdPc, ptdOther } = acc[key];
            batch.claimAmount =
              batch.claimableCs * ptdCs +
              batch.claimablePc * ptdPc +
              batch.claimableOther * ptdOther;
            return acc;
          }, {});

          responseArray = Object.values(groupedBySkuAndReference).map((item: any, index: number) => ({
            ...item,
            id: index + 1,
            claimAmount: `${currency} ${formatDecimal(item.claimAmount)}`,
            approvedClaimAmount: `${currency} ${formatDecimal(item.approvedClaimAmount)}`,
          }));
        } else if (formatClaimSubType(claimSubType) === claimSubTypes[1]) {
          const groupedBySkuCode = updatedClaimDetails.reduce((acc: any, detail: any) => {
            const skuCode = detail.skuCode;
            const stockType = enableFocPurchaseReturnClaims ? detail.extendedAttributes?.stockType : "";
            const key = `${skuCode}-${stockType}`;
            const imgBlobUrl = blobUrlMap[skuCode];
            if (displayOtherUnitName) {
              const product = products[skuCode];
              if (product?.otherUnitName && product.otherUnitName.length > 0) {
                customOtherUnitLabel = product.otherUnitName;
              }
            }
            const ptdValues = batchIdConfiguration
            ? {}
            : {
                ptdCs: detail.extendedAttributes.ptdCs,
                ptdPc: detail.extendedAttributes.ptdPc,
                ptdOther: detail.extendedAttributes.ptdOther,
              };
            if (!acc[key]) {
              acc[key] = {
                skuCode,
                skuName: detail.skuName,
                stockType: stockType,
                ...ptdValues,
                batchIds: [],
                damageCase: 0,
                damagePcs: 0,
                damageOthers: 0,
                approvedCase: 0,
                approvedPcs: 0,
                approvedOthers: 0,
                claimAmount: 0,
                approvedClaimAmount: 0,
                imgBlobUrl,
                otherUnitName: customOtherUnitLabel
              };
            }

            const [damageCases, damagePcs, damageOthers] = detail.claimedQty.split(".").map(Number);
            const [approvedCases, approvedPcs, approvedOthers] = detail.approvedQty.split(".").map(Number);
            acc[key].damageCase += damageCases || 0;
            acc[key].damagePcs += damagePcs || 0;
            acc[key].damageOthers += damageOthers || 0;
            acc[key].approvedCase += approvedCases || 0;
            acc[key].approvedPcs += approvedPcs || 0;
            acc[key].approvedOthers += approvedOthers || 0;
            acc[key].claimAmount += detail.claimedAmount || 0;
            acc[key].approvedClaimAmount += detail.approvedAmount || 0;

            let batch = acc[key].batchIds.find((b: any) => b.batchId === detail.batchCode);
            if (!batch) {
              const ptdValues = batchIdConfiguration ?
                {
                  ptdCs: detail.extendedAttributes.ptdCs,
                  ptdPc: detail.extendedAttributes.ptdPc,
                  ptdOther: detail.extendedAttributes.ptdOther,
                } : {};
              batch = {
                batchId: detail.batchCode,
                claimableCs: 0,
                claimablePc: 0,
                claimableOther: 0,
                approvedCs: 0,
                approvedPc: 0,
                approvedOther: 0,
                returnReasonArray: [],
                claimsRejectionReason: detail.rejectionReason,
                claimDetailsIds: [],
                approvals: [],
                ...ptdValues
              };
              acc[key].batchIds.push(batch);
            }

            batch.returnReasonArray.push({
              cs: damageCases || 0,
              pc: damagePcs || 0,
              other: damageOthers || 0,
              approvedCs: approvedCases || 0,
              approvedPc: approvedPcs || 0,
              approvedOther: approvedOthers || 0,
              reason: detail.remark,
              claimDetailsId: detail.id,
              approvals: detail.approvals
            });

            batch.claimableCs += damageCases || 0;
            batch.claimablePc += damagePcs || 0;
            batch.claimableOther += damageOthers || 0;
            batch.approvedCs += approvedCases || 0;
            batch.approvedPc += approvedPcs || 0;
            batch.approvedOther += approvedOthers || 0;
            batch.claimDetailsIds.push(detail.id);
            batch.approvals.push(...detail.approvals);
            const { ptdCs, ptdPc, ptdOther } = acc[key];
            if (batchIdConfiguration) {
              batch.claimAmount =
                batch.claimableCs * (batch.ptdCs || 0) +
                batch.claimablePc * (batch.ptdPc || 0) +
                batch.claimableOther * (batch.ptdOther || 0);
            } else {
            batch.claimAmount =
              batch.claimableCs * ptdCs +
              batch.claimablePc * ptdPc +
              batch.claimableOther * ptdOther;  
            }
            return acc;
          }, {});

          responseArray = Object.values(groupedBySkuCode).map((item: any, index: number) => ({
            ...item,
            id: index + 1,
            claimAmount: `${currency} ${formatDecimal(item.claimAmount)}`,
            approvedClaimAmount: `${currency} ${formatDecimal(item.approvedClaimAmount)}`,
          }));
        }
        else if (formatClaimSubType(claimSubType) === claimSubTypes[2]) {
          const groupedBySkuAndRemark = updatedClaimDetails.reduce((acc: any, detail: any) => {
            const skuCode = detail.skuCode;
            const remark = detail.remark;
            const key = `${skuCode}-${remark}`;
            const imgBlobUrl = blobUrlMap[skuCode];
            if (displayOtherUnitName) {
              const product = products[skuCode];
              if (product?.otherUnitName && product.otherUnitName.length > 0) {
                customOtherUnitLabel = product.otherUnitName;
              }
            }
            const batchId = batchIdConfiguration ? detail.batchCode : '';
            if (!acc[key]) {
              acc[key] = {
                skuCode,
                skuName: detail.skuName,
                invoices: [],
                damageCase: 0,
                damagePcs: 0,
                damageOthers: 0,
                approvedCase: 0,
                approvedPcs: 0,
                approvedOthers: 0,
                claimAmount: 0,
                approvedClaimAmount: 0,
                remark,
                imgBlobUrl,
                otherUnitName: customOtherUnitLabel
              };
            }
            const [damageCases, damagePcs, damageOthers] = detail.claimedQty.split(".").map(Number);
            const [approvedCases, approvedPcs, approvedOthers] = detail.approvedQty.split(".").map(Number);

            acc[key].damageCase += damageCases || 0;
            acc[key].damagePcs += damagePcs || 0;
            acc[key].damageOthers += damageOthers || 0;
            acc[key].approvedCase += approvedCases || 0;
            acc[key].approvedPcs += approvedPcs || 0;
            acc[key].approvedOthers += approvedOthers || 0;
            acc[key].claimAmount += detail.claimedAmount || 0;
            acc[key].approvedClaimAmount += detail.approvedAmount || 0;

            const invoiceEntry = {
              id: `${skuCode}-${detail.referenceNumber}-${remark}${batchIdConfiguration ? `-${batchId}` : ''}`,
              invoiceNumber: detail.referenceNumber,
              outletName: detail.outletName || "-",
              outletCode: detail.outletCode || "-",
              cases: damageCases || 0,
              pieces: damagePcs || 0,
              others: damageOthers || 0,
              approvedCs: approvedCases || 0,
              approvedPc: approvedPcs || 0,
              approvedOther: approvedOthers || 0,
              discount: detail.claimedAmount || 0,
              claimsRejectionReason: detail.rejectionReason,
              totalAmount: detail.extendedAttributes.totalAmount,
              claimDetailsId: detail.id,
              approvals: detail.approvals,
              batchIds: batchId
            };
            acc[key].invoices.push(invoiceEntry);
            return acc;
          }, {});

          responseArray = Object.values(groupedBySkuAndRemark).map((item: any, index: number) => ({
            ...item,
            id: index + 1,
            claimAmount: `${currency} ${formatDecimal(item.claimAmount)}`,
            approvedClaimAmount: `${currency} ${formatDecimal(item.approvedClaimAmount)}`,
          }));
        }
        else if (formatClaimSubType(claimSubType) === claimSubTypes[3]) {
          responseArray = updatedClaimDetails.map((detail: any, index: number) => {
            const [damageCases, damagePcs, damageOthers] = detail.claimedQty.split(".").map(Number);
            const [approvedCases, approvedPcs, approvedOthers] = detail.approvedQty.split(".").map(Number);
            return {
              id: index + 1,
              damageCases,
              damagePcs,
              damageOthers,
              approvedCases,
              approvedPcs,
              approvedOthers,
              invoiceDateTime: detail.extendedAttributes.invoiceDateTime,
              displayStartDate: detail.displayStartDate || "-",
              displayEndDate: detail.displayEndDate || "-",
              invoiceNumber: detail.referenceNumber || "-",
              batchCode: detail.batchCode || "-",
              claimAmount: `${currency} ${formatDecimal(detail.claimedAmount)}`,
              approvedClaimAmount: `${currency} ${formatDecimal(detail.approvedAmount)}`,
              claimsRejectionReason: detail.rejectionReason,
              claimDetailsId: detail.id,
              approvals: detail.approvals,
            };
          });
        }
        else if (formatClaimSubType(claimSubType) === claimSubTypes[4]) {
          responseArray = claimDetails.map((detail: any, index: number) => {
            const skus = detail.extendedAttributes?.skus || [];
            const formattedSkus = skus.map((sku: any) => `${sku.skuCode} - ${sku.skuName}`);

            return {
              id: index + 1,
              outletCode: detail.outletCode || "-",
              outletName: detail.outletName || "-",
              skus: formattedSkus,
              assetApprovedQty: Number(detail.approvedQty) || 0,
              displayStartDate: detail.displayStartDate || "-",
              displayEndDate: detail.displayEndDate || "-",
              batchCode: detail.batchCode || "-",
              claimAmount: `${currency} ${formatDecimal(detail.claimedAmount)}`,
              approvedClaimAmount: `${currency} ${formatDecimal(detail.approvedAmount)}`,
              remark: detail.remark,
              blobUrl: detail.blobUrl,
              claimsRejectionReason: detail.rejectionReason,
            };
          });
        }
        else {
          responseArray = claimDetails.map((detail: any, index: number) => {

            return {
              id: index + 1,
              outletCode: detail.outletCode || "-",
              outletName: detail.outletName || "-",
              assetType: detail.assetType || "-",
              assetId: detail.assetId || "-",
              assetQty: detail.assetQty || 0,
              assetApprovedQty: Number(detail.approvedQty) || 0,
              displayStartDate: detail.displayStartDate || "-",
              displayEndDate: detail.displayEndDate || "-",
              batchCode: detail.batchCode || "-",
              claimAmount: `${currency} ${formatDecimal(detail.claimedAmount)}`,
              approvedClaimAmount: `${currency} ${formatDecimal(detail.approvedAmount)}`,
              remark: detail.remark,
              blobUrl: detail.blobUrl,
              claimsRejectionReason: detail.rejectionReason,
            };
          });
        }
        responseArray = sortRows(
          responseArray,
          claimData.claimType,
          claimData.claimSubType,
        );

        setAccordionData(responseAccordion);
        setData(responseArray);
        dispatch(setViewClaimsRow(responseArray));
        dispatch(setSelectedViewClaimsRow(responseArray[0]));

        if (formatClaimSubType(claimSubType) !== claimSubTypes[3]) { setIsModalOpen(true); }
      } else {
        throw new Error("No claim data found");
      }
    } catch (error) {
      setData([]);
      openPopup("Something went wrong", "Error");
    }
  };

  const init = async () => {
    if (!claimId) {
      return;
    }
    setLoader(true);
    try {
      await Promise.allSettled([
        getAllDataForClaim(claimId),
        getActionButtons()
      ]);
    } finally {
      setLoader(false);
    }
  };

  const updateSelectedRowInRows = (updatedRow: any) => {
    dispatch(setSelectedViewClaimsRow(updatedRow));
    if (viewClaimsRows) {
      const updatedRows = viewClaimsRows.map((row: any) =>
        row.id === updatedRow.id ? updatedRow : row
      );
      dispatch(setViewClaimsRow(updatedRows));
    }
  };

  const handleRowClick = (params: any) => {
    dispatch(setSelectedViewClaimsRow(params.row));
    setIsModalOpen(true);
  };

  const handleOpenModal = (row: any) => {
    dispatch(setSelectedViewClaimsRow(row));
    setIsStatusModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsStatusModalOpen(false);
    setSelectedViewClaimsRow(null);
  };

  useEffect(() => {
    logLoadTrace(init, "view_claim_details_page_load_time");
  }, [claimId]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
  
    const handler = createScrollHandler(
      scrollRef,
      isScrolling,
      setAllowTableScroll,
      setDontContainerScroll
    );
  
    el.addEventListener("wheel", handler);
  
    return () => {
      el.removeEventListener("wheel", handler);
    };
  }, [isScrolling, dontAllowContainerScroll]);
  
  if (loader) {
    return <Loader />;
  }

  if (!loader && Object.keys(data).length === 0) {
    return <h3>No information exist with id: {claimId}</h3>;
  }

  return (
    <>
      {isStatusModalOpen && (
        <ClaimDetailsModal
          isOpen={isStatusModalOpen}
          onClose={handleCloseModal}
          claimId={claimId}
          status={accordionData.status}
          claimType={accordionData.claimType}
          typeOfExpense={accordionData.typeOfExpense}
          data={selectedViewClaimsRow}
          statusConfig={actionButtons}
          caseUnitLabel={caseUnitLabel}
          otherUnitLabel={otherUnitLabel}
          packUnitLabel={packUnitLabel}
          approvalConfig={approvalConfig}
        />
      )}

      <ConfirmationPopUp
        openConfirmModal={confirmationPopUp}
        message={popUpMessage}
        setOpenConfirmModal={() => {
          setConfirmationPopUp(false);
        }}
        successMethod={popUpSuccessMethod.current}
      />
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      <div className="view-primary-order-parent-vo" style={{width: "100%", height: "100%"}} onWheel={(e) => handleScroll(e, setIsScrolling, scrollTop)}>
        <div className="view-primary-order-header">
          <FormHeader
            parentLabel={"View Claim Status"}
            label={"Claim ID Details"}
            goBackToParent={() => navigate(-1)}
          />
          <div>
            <TextField
              id="outlined-basic"
              label="Search"
              variant="outlined"
              size="small"
              sx={{ width: "230px" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <div className="search-icon-wrapper">
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                    </div>
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
              onChange={(e) => {
                filterData(e.target.value);
              }}
            />
          </div>
        </div>
        <div style={{
          display: "flex",
          height: "100%",
          gap: "12px"
        }}>
          <div style={{
            width: isModalOpen ? "55%" : "100%",
          }}>
            <div ref={scrollRef} style={{
                gap: "14px",
                overflow: (dontAllowContainerScroll) ? "hidden" : "scroll",
                height: "100%",
                scrollbarWidth: "none", // Firefox
                msOverflowStyle: "none", // IE 10+
              }}
            >
              <CustomAccordian
                expanded={!isScrolling}
                onCustomAccordionToggle={onCustomAccordionToggle}
                isSecondHalf={true}
                metaData1={viewClaimMetaData1}
                data1={accordionData}
                metaData2={viewClaimMetaData2(currency, accordionData?.status, accordionData?.claimType, accordionData?.typeOfExpense, actionButtons)}
                data2={accordionData}
                label1=""
                metaData1ChunkSize={5}
                metaData2ChunkSize={5}
                header={`Claim ID. ${claimId}`}
                statusConfig={actionButtons}
                status={accordionData?.status}
                singleColumnInEachHalf={true}
              />
            <div style={{height: "100%", position: "sticky", top: "0"}}>
            <div className="view_order_details_vo">
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "flex-start",
                  marginTop: "0.75rem"
                }}
              >
                <Header title={"View Details"} />
              </div>
              {loader ? (
                <Loader />
              ) : (
                <CustomDataGrid
                  rows={rows}
                  columnsMetaData={claimsDetailsColumnsMetaData}
                  {
                  ...(accordionData?.claimType === claimTypes[2]
                    ? {
                      onEditClick: handleOpenModal,
                      onRowClick: handleRowClick,
                      selectedRowId: selectedViewClaimsRow?.id,
                      insideOpenClaimsDetails: true,
                    }
                    :
                    accordionData?.typeOfExpense !== claimSubTypes[3]
                      ? {
                        onRowClick: handleRowClick,
                        selectedRowId: selectedViewClaimsRow?.id,
                        insideOpenClaimsDetails: true,
                      }
                      : {
                        onEditClick: handleOpenModal,
                        insideOpenClaimsDetails: false
                      })
                  }
                  tableWidth={sidebarCollapsed && isModalOpen ? 0.45 : 0.75}
                  enableRowClickRedirect={false}
                />
              )}
              </div>
            </div>
          </div>
          </div>
          <ViewClaimsDetailsModal
            key={selectedViewClaimsRow?.id}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              dispatch(setSelectedViewClaimsRow(null));
            }}
            accordionData={accordionData}
            updateSelectedRowInRows={updateSelectedRowInRows}
            statusConfig={actionButtons}
            status={accordionData?.status}
            approvalConfig={approvalConfig}
          />
        </div>
      </div>
    </>
  );
};

export default ViewClaimsDetails;